
import React from 'react';
import { Send } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

// Animation variants for the send button
const sendButtonVariants = {
  idle: { 
    scale: 1,
    rotate: 0 
  },
  active: { 
    scale: 1.1,
    rotate: 45, // 45 degrees to make it face up-right
    transition: { 
      duration: 0.1, // Faster transition for immediate feedback
      ease: "easeOut" 
    } 
  },
  loading: {
    rotate: 360,
    transition: {
      repeat: Infinity,
      duration: 1.5,
      ease: "linear"
    }
  }
};

interface SendButtonProps {
  isLoading: boolean;
  disabled: boolean;
  messageText: string;
}

export const SendButton: React.FC<SendButtonProps> = ({ 
  isLoading, 
  disabled, 
  messageText 
}) => {
  // Better validation that handles emojis properly
  const hasContent = messageText && messageText.trim().length > 0;
  
  // Determine button state based on props
  const buttonState = isLoading 
    ? 'loading' 
    : hasContent 
      ? 'active' 
      : 'idle';
  
  return (
    <motion.button
      type="submit"
      disabled={!hasContent || isLoading || disabled}
      className={cn(
        // Use simple flex centering with fixed dimensions
        "flex items-center justify-center",
        "rounded-full",
        "w-8 h-8", // Smaller size
        "bg-blue-500 hover:bg-blue-600 text-white",
        "disabled:opacity-60 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:text-slate-500",
        "transition-colors duration-200"
      )}
      initial="idle"
      animate={buttonState}
      variants={sendButtonVariants}
      whileTap={{ scale: 0.9 }}
    >
      <Send 
        size={16} // Slightly smaller icon
        strokeWidth={1.5} 
        className="m-0" // Ensure no margin affects centering
      />
    </motion.button>
  );
};

export default SendButton;
